import { type LucideIcon, Search } from 'lucide-react';
import * as React from 'react';

import { Badge } from '@/design-system/components/ui/badge';
import { Button } from '@/design-system/components/ui/button';
import { ChipTrigger } from '@/design-system/components/ui/chip-trigger';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuSwitchItem,
  DropdownMenuTrigger,
} from '@/design-system/components/ui/dropdown-menu';
import { Input } from '@/design-system/components/ui/input';
import { Text } from '@/design-system/components/ui/text';

export interface OptionsDropdownItem {
  id: string;
  label: string;
}

export interface OptionsDropdownProps {
  /** All available options. */
  options: OptionsDropdownItem[];
  /** Set of currently-checked option ids. */
  value: Set<string>;
  /** Replace the checked set. Fully controlled. */
  onValueChange: (next: Set<string>) => void;

  triggerLabel?: string;
  triggerIcon?: LucideIcon;
  className?: string;
  searchable?: boolean;
  searchPlaceholder?: string;

  /**
   * Default value. When provided, a "Reset" link appears in the header
   * whenever `value` differs from `defaultValue`. Resets value to it on click.
   */
  defaultValue?: Set<string>;

  /**
   * Render the trigger badge. Receives (checkedCount, total).
   * Default: hides the badge while `value` matches `defaultValue` (or while
   * value is empty when no defaultValue is given); otherwise shows the
   * checked count. Return null to hide. Useful for opt-in and opt-out cases
   * without customising.
   */
  formatBadge?: (checked: number, total: number) => React.ReactNode;

  /**
   * Render the header subtitle. Receives (checkedCount, total).
   * Default: hides the subtitle.
   */
  formatSubtitle?: (checked: number, total: number) => React.ReactNode;

  selectAllLabel?: string;
  deselectAllLabel?: string;
  resetLabel?: string;
}

const setEquals = (a: Set<string>, b: Set<string>) => {
  if (a.size !== b.size) return false;
  for (const x of a) if (!b.has(x)) return false;
  return true;
};

export const OptionsDropdown = ({
  options,
  value,
  onValueChange,
  triggerLabel = 'Options',
  triggerIcon,
  className,
  searchable = true,
  searchPlaceholder = 'Search…',
  defaultValue,
  formatBadge,
  formatSubtitle,
  selectAllLabel = 'Select all',
  deselectAllLabel = 'Deselect all',
  resetLabel = 'Reset',
}: OptionsDropdownProps) => {
  const [open, setOpen] = React.useState(false);
  const [query, setQuery] = React.useState('');

  const total = options.length;
  const checked = value.size;

  const filtered = React.useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) => o.label.toLowerCase().includes(q));
  }, [options, query]);

  const toggle = (id: string) => {
    const next = new Set(value);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    onValueChange(next);
  };

  const selectAll = () => onValueChange(new Set(options.map((o) => o.id)));
  const deselectAll = () => onValueChange(new Set());
  const reset = () => onValueChange(defaultValue ?? new Set());

  const isAtDefault = defaultValue ? setEquals(value, defaultValue) : checked === 0;
  const badge =
    formatBadge?.(checked, total) ??
    (isAtDefault ? null : (
      <Badge variant="default" size="sm">
        {checked}
      </Badge>
    ));
  const isActive = badge !== null && badge !== undefined && badge !== false;
  const subtitle = formatSubtitle?.(checked, total);
  const canReset = defaultValue !== undefined && !isAtDefault;

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <ChipTrigger
          icon={triggerIcon}
          label={triggerLabel}
          size="small"
          active={isActive}
          onClear={canReset ? reset : undefined}
          className={className}
        >
          {badge}
        </ChipTrigger>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="end" sideOffset={6} className="tw:w-[280px]">
        <DropdownMenuLabel className="tw:flex tw:items-center tw:gap-2">
          <Text variant="body-sm" weight="medium" as="span">
            {triggerLabel}
          </Text>
          {subtitle && (
            <Text variant="caption" color="muted" as="span">
              {subtitle}
            </Text>
          )}
          {canReset && (
            <Button variant="link-muted" size="xs" onClick={reset} className="tw:ml-auto">
              {resetLabel}
            </Button>
          )}
        </DropdownMenuLabel>

        {searchable && (
          <>
            <DropdownMenuSeparator />
            <div className="tw:p-1.5">
              <Input
                size="sm"
                placeholder={searchPlaceholder}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                aria-label={searchPlaceholder}
                leftSlot={<Search className="tw:size-4 tw:text-muted-foreground" aria-hidden="true" />}
              />
            </div>
          </>
        )}

        <DropdownMenuSeparator />

        <div className="tw:max-h-[280px] tw:overflow-auto">
          {filtered.length === 0 ? (
            <DropdownMenuLabel>
              <Text variant="body-sm" color="muted" as="span">
                No matches
              </Text>
            </DropdownMenuLabel>
          ) : (
            filtered.map((opt) => {
              const isOn = value.has(opt.id);
              return (
                <DropdownMenuSwitchItem key={opt.id} checked={isOn} onCheckedChange={() => toggle(opt.id)}>
                  <Text variant="body-sm" color={isOn ? 'default' : 'muted'} as="span">
                    {opt.label}
                  </Text>
                </DropdownMenuSwitchItem>
              );
            })
          )}
        </div>

        <DropdownMenuSeparator />

        <div className="tw:flex tw:items-center tw:gap-3 tw:px-3 tw:py-2">
          <Button variant="link-muted" size="xs" onClick={selectAll}>
            {selectAllLabel}
          </Button>
          <Button variant="link-muted" size="xs" onClick={deselectAll}>
            {deselectAllLabel}
          </Button>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

OptionsDropdown.displayName = 'OptionsDropdown';
